import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import SeoHead from '../components/SeoHead';
import Footer from '../components/Footer';

const Status = () => {
  const [systemStatus, setSystemStatus] = useState({
    backend: { status: 'checking', message: 'Vérification...', lastCheck: null },
    database: { status: 'checking', message: 'Vérification...', lastCheck: null }
  });

  const checkBackendHealth = async () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    
    try {
      const response = await fetch(`${apiUrl}/health`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      const now = new Date().toLocaleString('fr-FR');

      // L'API a répondu (même avec un code 503), on analyse le statut détaillé
      setSystemStatus({
        backend: {
          status: data.api?.status === 'ok' ? 'operational' : 'degraded',
          message: data.api?.status === 'ok' ? 'Opérationnel' : 'Dégradé',
          lastCheck: now,
          uptime: data.uptime ? Math.floor(data.uptime / 60) : 0
        },
        database: {
          status: data.database?.status === 'connected' ? 'operational' : 'degraded',
          message: data.database?.status === 'connected' ? 'Connectée' : 'Dégradée',
          lastCheck: now
        }
      });
    } catch (error) {
      const now = new Date().toLocaleString('fr-FR');
      setSystemStatus({
        backend: {
          status: 'offline',
          message: 'Hors ligne',
          lastCheck: now
        },
        database: {
          status: 'offline',
          message: 'Hors ligne',
          lastCheck: now
        }
      });
    }
  };

  useEffect(() => {
    checkBackendHealth();
    const interval = setInterval(checkBackendHealth, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational':
        return 'green';
      case 'degraded':
        return 'yellow';
      case 'offline':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational':
        return (
          <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'degraded':
        return (
          <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'offline':
        return (
          <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="h-5 w-5 text-gray-400 animate-spin" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        );
    }
  };

  const allOperational = systemStatus.backend.status === 'operational' && systemStatus.database.status === 'operational';

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900">
      <SeoHead
        title="Statut du service PulseCIO - Disponibilité, sécurité et performance de la messagerie SaaS professionnelle"
        description="Consultez le statut en temps réel de PulseCIO, plateforme SaaS de messagerie professionnelle et collaborative. Vérifiez la disponibilité, la sécurité et la performance de notre application cloud pour la gestion des messages d'entreprise."
        breadcrumb={{ name: "Statut", path: "/status" }}
      />
      <Navbar />
      <main className="flex-1 container mx-auto px-6 py-20">
        <h1 className="text-3xl font-bold mb-6 text-black dark:text-white">Statut du service PulseCIO : disponibilité et sécurité de la messagerie SaaS</h1>
        <div className="prose dark:prose-invert max-w-none">
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Suivi en temps réel de la disponibilité, de la sécurité et des performances de PulseCIO, votre plateforme SaaS de messagerie professionnelle et collaborative. Consultez les incidents récents, la maintenance planifiée et l'état de nos serveurs cloud pour garantir la continuité de vos communications d'entreprise.
          </p>
          
          {/* Overall Status Banner */}
          <div className={`bg-${getStatusColor(allOperational ? 'operational' : 'degraded')}-50 dark:bg-${getStatusColor(allOperational ? 'operational' : 'degraded')}-900/20 border border-${getStatusColor(allOperational ? 'operational' : 'degraded')}-200 dark:border-${getStatusColor(allOperational ? 'operational' : 'degraded')}-800 rounded-lg p-4 mt-6`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {getStatusIcon(allOperational ? 'operational' : systemStatus.backend.status)}
              </div>
              <div className="ml-3">
                <h2 className={`text-sm font-medium text-${getStatusColor(allOperational ? 'operational' : 'degraded')}-800 dark:text-${getStatusColor(allOperational ? 'operational' : 'degraded')}-200`}>
                  {allOperational ? 'Tous les systèmes opérationnels' : 'Certains systèmes rencontrent des problèmes'}
                </h2>
                <p className={`text-sm text-${getStatusColor(allOperational ? 'operational' : 'degraded')}-700 dark:text-${getStatusColor(allOperational ? 'operational' : 'degraded')}-300 mt-1`}>
                  Dernière vérification : {systemStatus.backend.lastCheck || 'En cours...'}
                </p>
              </div>
            </div>
          </div>

          {/* Components Status */}
          <div className="mt-8">
            <h2 className="text-2xl font-bold mb-4 text-black dark:text-white">Composants système</h2>
            <div className="space-y-3">
              {/* Backend API */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      {getStatusIcon(systemStatus.backend.status)}
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                        API Backend
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {systemStatus.backend.message}
                        {systemStatus.backend.uptime && ` • Uptime: ${systemStatus.backend.uptime} min`}
                      </p>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {systemStatus.backend.lastCheck}
                  </div>
                </div>
              </div>

              {/* Database */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      {getStatusIcon(systemStatus.database.status)}
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                        Base de données (MongoDB)
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {systemStatus.database.message}
                      </p>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {systemStatus.database.lastCheck}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Refresh Button */}
          <div className="mt-6">
            <button
              onClick={checkBackendHealth}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Actualiser le statut
            </button>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Le statut est vérifié automatiquement toutes les 30 secondes
            </p>
          </div>
        </div>
      </main>
      <div className="w-full flex justify-center mt-12 mb-2">
        <a href="/" className="inline-flex items-center px-5 py-2 rounded-lg bg-blue-600 text-white font-semibold shadow hover:bg-blue-700 transition">
          ← Retour à la page principale
        </a>
      </div>
      <Footer />
    </div>
  );
};

export default Status;